import {
  Button,
  Group,
  Input,
  Modal,
  Stack,
  Text,
  Textarea,
} from "@assetwatch/ui";
import { useEffect, useState } from "react";

type AddTeamModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: { name: string; description?: string }) => Promise<void>;
  isSaving: boolean;
};

export function AddTeamModal({
  open,
  onOpenChange,
  onCreate,
  isSaving,
}: AddTeamModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fields are cleared on open (rather than on close) so the modal doesn't
  // visibly reset itself while its close animation is still playing.
  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setError(null);
    }
  }, [open]);

  async function addTeam() {
    const trimmedName = name.trim();
    if (!trimmedName || isSaving) return;
    setError(null);
    try {
      await onCreate({
        name: trimmedName,
        description: description.trim() || undefined,
      });
      onOpenChange(false);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to create team.",
      );
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add team"
      description="Create a new work team for this facility."
      size="md"
      footer={
        <Group justify="end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim() || isSaving}
            loading={isSaving}
            loadingText="Adding"
            variant="primary"
            onClick={addTeam}
          >
            Add team
          </Button>
        </Group>
      }
    >
      <Stack gap={16}>
        <Input
          label="Team name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          label="Description"
          maxLength={500}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        {error ? <Text tone="negative">{error}</Text> : null}
      </Stack>
    </Modal>
  );
}
